import React, { useCallback, useEffect, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import {
  fetchMyLuckyWheelSpins,
  type LuckyWheelMySpin,
  type LuckyWheelPaymentType,
} from '../../services/luckyWheelService'
import { HistoryCollapseCard, type HistoryDetailRow } from './HistoryCollapseCard'
import type { HistoryOutletContext } from './ModernHistoryLayout'

const PAGE_SIZE = 20

const formatNumber = (value: number | null | undefined, digits = 0) =>
  Number(value || 0).toLocaleString('th-TH', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('th-TH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const unitFor = (paymentType: LuckyWheelPaymentType | string) =>
  paymentType === 'cash' ? 'บาท' : 'คะแนน'

const digitsFor = (paymentType: LuckyWheelPaymentType | string) =>
  paymentType === 'cash' ? 2 : 0

const buildRows = (item: LuckyWheelMySpin): HistoryDetailRow[] => {
  const unit = unitFor(item.paymentType)
  const digits = digitsFor(item.paymentType)
  const prizeUnit = unitFor(item.prizeCurrency || item.paymentType)
  const prizeDigits = digitsFor(item.prizeCurrency || item.paymentType)

  return [
    { label: 'โหมด', value: item.modeKey },
    {
      label: 'ช่องทาง',
      value:
        item.paidWith === 'ticket'
          ? 'ตั๋วเล่นฟรี'
          : item.paymentType === 'cash'
            ? 'เงินสด'
            : 'คะแนน',
    },
    {
      label: 'ค่าหมุน',
      value:
        item.paidWith === 'ticket'
          ? 'ฟรี'
          : `${formatNumber(item.costAmount, digits)} ${unit}`,
    },
    {
      label: 'ผล',
      value:
        item.kind === 'prize'
          ? `${formatNumber(item.prizeAmount, prizeDigits)} ${prizeUnit}`
          : item.segmentLabel || 'โชคไม่เข้า',
      tone: item.kind === 'prize' ? 'positive' : 'default',
    },
    { label: 'ชิ้นที่หยุด', value: item.segmentLabel || '-' },
    { label: 'เวลา', value: formatDateTime(item.createdAt) },
  ]
}

/** แท็บประวัติเกมส์วงล้อมหาโชค */
const HistoryLuckyWheelPage: React.FC = () => {
  useOutletContext<HistoryOutletContext>()
  const [items, setItems] = useState<LuckyWheelMySpin[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadHistory = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetchMyLuckyWheelSpins({ page: pageNum, limit: PAGE_SIZE })
      setItems(response.data || [])
      setPage(response.pagination?.page || pageNum)
      setTotalPages(Math.max(1, response.pagination?.totalPages || 1))
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'โหลดประวัติไม่สำเร็จ')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadHistory(1)
  }, [loadHistory])

  const handleCopyRef = async (refId: string) => {
    try {
      await navigator.clipboard.writeText(refId)
    } catch {
      // ignore
    }
  }

  return (
    <div className="htd-hist__stack">
      <h2 className="htd-hist__page-title">ประวัติเกมส์วงล้อมหาโชค</h2>

      <div className="htd-hist__panel">
        <div className="htd-hist__summary">
          <div>
            <strong>รายการหมุนวงล้อ</strong>
            <span>ค่าหมุน · ผลรางวัล · ช่องทางชำระ</span>
          </div>
          <div className="text-end">
            <Link to="/points/games/lucky-wheel" className="htd-hist__inline-link">
              ไปเล่นวงล้อ
            </Link>
          </div>
        </div>
      </div>

      <div className="htd-hist__list">
        {loading && (
          <div className="htd-hist__loading">
            <h4>
              <i className="fas fa-spinner fa-spin me-2" />
              กำลังโหลดประวัติวงล้อ...
            </h4>
          </div>
        )}

        {!loading && error && (
          <div className="htd-hist__shell">
            <h4>{error}</h4>
            <button type="button" className="htd-hist__retry-btn" onClick={() => void loadHistory(page)}>
              ลองโหลดอีกครั้ง
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="htd-hist__empty">
            <h4>ยังไม่มีประวัติวงล้อ</h4>
            <p>เมื่อหมุนวงล้อมหาโชค รายการจะแสดงที่นี่</p>
            <Link to="/points/games/lucky-wheel" className="htd-hist__inline-link">
              ไปเล่นวงล้อ
            </Link>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="htd-hist__panel mb-2">
              <div className="htd-hist__summary">
                <div>
                  <strong>
                    พบ {items.length} รายการ
                    {totalPages > 1 ? ` (หน้า ${page}/${totalPages})` : ''}
                  </strong>
                  <span>แตะการ์ดเพื่อดูรายละเอียด</span>
                </div>
              </div>
            </div>

            {items.map((item) => {
              const digits = digitsFor(item.prizeCurrency || item.paymentType)
              const leftIcon = item.kind === 'prize' ? 'success' : 'reject'
              return (
                <HistoryCollapseCard
                  key={item.spinId}
                  id={item.spinId}
                  leftIcon={leftIcon}
                  refId={item.spinId}
                  onCopyRef={() => void handleCopyRef(item.spinId)}
                  rows={buildRows(item)}
                  header={(
                    <div className="w-100 pe-md-4">
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>
                        {item.modeKey} ·{' '}
                        {item.kind === 'prize'
                          ? `ได้ ${formatNumber(item.prizeAmount, digits)} ${unitFor(item.prizeCurrency || item.paymentType)}`
                          : item.segmentLabel || 'โชคไม่เข้า'}
                      </div>
                      <small style={{ color: 'rgba(186,230,253,0.75)', display: 'block' }}>
                        {item.paidWith === 'ticket'
                          ? 'ตั๋วฟรี'
                          : `ค่าหมุน ${formatNumber(item.costAmount, digitsFor(item.paymentType))} ${unitFor(item.paymentType)}`}
                        {' · '}
                        {formatDateTime(item.createdAt)}
                      </small>
                    </div>
                  )}
                />
              )
            })}

            {totalPages > 1 && (
              <div className="d-flex justify-content-center gap-2 mt-2">
                <button
                  type="button"
                  className="htd-hist__retry-btn"
                  disabled={page <= 1}
                  onClick={() => void loadHistory(page - 1)}
                >
                  ก่อนหน้า
                </button>
                <button
                  type="button"
                  className="htd-hist__retry-btn"
                  disabled={page >= totalPages}
                  onClick={() => void loadHistory(page + 1)}
                >
                  ถัดไป
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default HistoryLuckyWheelPage
